//
// Copyright (c) 2011-2021 by Curalate, Inc.
//

import React, { useEffect } from 'react';
import styled from 'styled-components';

import ActionButton from '@components/atoms/action-button/ActionButton';
import HelpIcon from '@components/atoms/help-icon/HelpIcon';
import Text from '@components/atoms/text/Text';
import { HarmonyAIIcon } from '@components/harmonyai/HarmonyAIIcon';
import { useSmartInputContext } from '@components/molecules/smart-input/SmartInputContext';
import { HashtagButton } from '@components/molecules/smart-input/recommended-hashtags/HashtagButton';
import { insertHashtag } from '@components/molecules/smart-input/recommended-hashtags/hashtagUtils';
import { IRecommendedHashtag } from '@components/molecules/smart-input/smartInputModel';
import { SmartInputTrackingId } from '@components/molecules/smart-input/smartInputTrackingIds';
import { getSmartInputTrackingId } from '@components/molecules/smart-input/smartInputUtils';
import { usePrevious } from '@hooks/usePrevious';
import { Mixins } from '@theme/mixins';

interface Props {
  recommendations: IRecommendedHashtag[];
}

export const HashtagDropdown = ({ recommendations }: Props) => {
  const smartInputContext = useSmartInputContext();

  const hasRecommendedHashtagsToShow = recommendations.some(
    (tag: IRecommendedHashtag) => tag.shouldShow,
  );

  const prevHasEnabledRecommendations = usePrevious(
    hasRecommendedHashtagsToShow,
  );

  // This effect allows this dropdown to close when there are no more recommendations to be added,
  // but open with the EmptyText when reopened
  useEffect(() => {
    if (prevHasEnabledRecommendations && !hasRecommendedHashtagsToShow) {
      closeDropdown();
    }
  });

  const closeDropdown = () => {
    smartInputContext.recommendedHashtags?.setIsDropdownOpen(false, true);
  };

  const handleHashtagClick = (tag: string) => {
    insertHashtag(tag, smartInputContext);
  };

  return (
    <DropdownWrapper tabIndex={-1}>
      <CloseButton
        iconName='close'
        onClick={closeDropdown}
        trackingId={getSmartInputTrackingId(
          smartInputContext.trackingIdPrefix,
          SmartInputTrackingId.CloseRecommenedHashtagsDropdownButton,
        )}
      />
      <TitleWrapper>
        <HarmonyAIIcon />
        <TitleText>Recommended Hashtags</TitleText>
        <HelpIcon
          appendTarget='body'
          helpText='Hashtag recommendations are based on your most commonly used hashtags.'
          placement='right'
        />
      </TitleWrapper>
      {hasRecommendedHashtagsToShow ? (
        <HashtagsContainer role='list'>
          {recommendations.map((tag) => (
            <HashtagButton
              key={tag.value}
              onClick={handleHashtagClick}
              recommendation={tag}
            />
          ))}
        </HashtagsContainer>
      ) : (
        <EmptyText mixin={Mixins.Text.UiCopySecondary}>
          You've added all of our recommended hashtags!
        </EmptyText>
      )}
    </DropdownWrapper>
  );
};

const DropdownWrapper = styled.div(
  ({ theme }) => `
  ${Mixins.Sheet.High}
  flex-direction: column;
  position: absolute;
  margin: 2px 0 0;
  padding: ${theme.spacings[3]} ${theme.spacings[3]} 0;
  width: 100%;
  
  /**
   * From design: This height accommodates 4.5 rows of labels. Given the average character count of a
   * hashtag, we expect ~2 labels to fit in each row, so ~8 full hashtags will be fully visible by
   * default. Brands are typically adding 3-4 hashtags per caption. This means that Bucky will easily
   * be able to visualize his top 3-4 captions that he's frequently re-using in captions 
   */
  max-height: 204px;
  min-height: ${theme.spacings[7]};
  overflow: hidden;
  z-index: ${theme.zindex.fixed};

  &:focus {
    outline: none;
  }
  `,
);

const CloseButton = styled(ActionButton)(
  ({ theme }) => `
  position: absolute;
  top: ${theme.spacings[2]};
  right: ${theme.spacings[2]};
`,
);

const TitleWrapper = styled.div(
  ({ theme }) => `
  align-self: flex-start;
  display: flex;
  align-items: center;
  margin: 0 0 ${theme.spacings[2]};
`,
);

const TitleText = styled(Text).attrs(() => ({
  mixin: Mixins.Text.UiCopy,
}))(
  ({ theme }) => `
  display: inline-block;
  margin: 0 ${theme.spacings[2]};
`,
);

const HashtagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  overflow: auto;
`;

const EmptyText = styled(Text)(
  ({ theme }) => `
  width: 100%;
  margin: 0 auto ${theme.spacings[2]};
  padding: ${theme.spacings[2]};
  text-align: center;
`,
);
